import type { MaybeRefOrGetter } from 'vue';
import { tryOnScopeDispose, useResizeObserver } from '@vueuse/core';
import { toValue } from 'vue';

/**
 * Room taken at the bottom of the window by the surface pinned in the toast corner. The stylesheet
 * adds the viewport's own inset on top, so this module never has to restate it.
 */
const TOAST_VIEWPORT_RESERVED = '--toast-viewport-reserved';

/**
 * Gap left between a pinned surface and the toasts above it. Mirrors the toast viewport's own inset
 * (`--spacing * 4`) so the corner is spaced alike all the way up.
 */
const SURFACE_INSET_PX = 16;

/** Live height of every pinned surface currently on screen. Absent means the surface is gone. */
const heights = new Map<symbol, number>();

function syncToastViewport() {
	const tallest = Math.max(0, ...heights.values());

	if (tallest === 0) {
		document.documentElement.style.removeProperty(TOAST_VIEWPORT_RESERVED);
		return;
	}

	document.documentElement.style.setProperty(TOAST_VIEWPORT_RESERVED, `${tallest + SURFACE_INSET_PX}px`);
}

/**
 * Raises the toast viewport past a surface pinned to the window's bottom corner, so toasts always
 * land clear of it as it grows and shrinks.
 *
 * The surface is released when the owning scope is disposed. Release it earlier from a leave
 * transition's *end* event — releasing the moment the surface starts leaving drops the toasts onto
 * one that is still sliding out.
 */
export function usePinnedSurface(target: MaybeRefOrGetter<HTMLElement | null | undefined>) {
	const surface = Symbol('pinned-surface');

	useResizeObserver(target, () => {
		const element = toValue(target);

		if (!element) return;

		heights.set(surface, element.offsetHeight);
		syncToastViewport();
	});

	function release() {
		heights.delete(surface);
		syncToastViewport();
	}

	tryOnScopeDispose(release);

	return { release };
}
